import React, { useEffect } from 'react';
import { animate } from 'animejs';
import './Home.css';

const Home = () => {
  useEffect(() => {
    // Scroll to top on component mount to prevent autoscroll
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    // First line: "Hi," - fade in and slide from left
    animate('.hero-greeting', {
      opacity: [0, 1],
      translateX: [-50, 0],
      duration: 800,
      easing: 'out-quad'
    });

    // Second line: "I'm George." - fade in and slide from left after first completes
    animate('.hero-name', {
      opacity: [0, 1],
      translateX: [-50, 0],
      delay: 800,
      duration: 800,
      easing: 'out-quad'
    });

    // Right section: description - fade in and slide from right after first two complete
    animate('.hero-description', {
      opacity: [0, 1],
      translateX: [50, 0],
      delay: 1600,
      duration: 800,
      easing: 'out-quad'
    });

    // Fade in the arrow indicator
    animate('.scroll-indicator', {
      opacity: [0, 1],
      delay: 2400,
      duration: 800,
      easing: 'out-quad'
    });
  }, []);

  // Sample case studies - you can replace these with your actual projects
  const caseStudies = [
    {
      id: 1,
      title: 'Digital Manufacturing Ireland',
      subtitle: 'Industrial UI & Production Line Systems',
      description: 'I designed all of the user interfaces powering the production lines at Digital Manufacturing Ireland (DMI), a facility primarily funded by the Government of Ireland. My focus was on creating highly functional, accessible interfaces that streamline complex industrial workflows, bridging the gap between advanced manufacturing technology and the operators on the floor. \n\n Throughout this project, I collaborated closely with the engineering team to ensure design feasibility and smooth handoffs. I also conducted hands-on user testing directly on the production floor, gathering critical feedback from operators to interate on the interfaces for maximum usability. \n\nThis project was a massive undertaking as there was ~134 different screens, which all needed to be exported and designed individually, due to the limitations of working with PLC systems.',
      image: new URL('../images/filling.png', import.meta.url).href,
      icon: new URL('../images/DMI.png', import.meta.url).href,
      tags: ['UI Design', 'UX Research', 'Figma']
    },
    {
      id: 2,
      title: 'Tirlán',
      subtitle: 'Process Control & Automation Interfaces',
      description: 'When working on this pasteurisation system, the biggest challenge was figuring out how to display a massive amount of inputs and outputs cleanly on a limited screen size. Because the process relies on precise temperature and timing controls, I had to design a dense layout that operators could still read at a single glance without getting overwhelmed by all the data points. \n\n Much of my time was spent running user testing sessions with the plant operators to see how they interacted with the screens in real time, which helped me cut out visual clutter and arrange the interface so that the most critical controls were always the easiest to reach.',
      image: new URL('../images/tirlanhmi.png', import.meta.url).href,
      icon: new URL('../images/tirlan.png', import.meta.url).href,
      tags: ['Mobile Design', 'iOS', 'Android']
    },
    {
      id: 3,
      title: 'Pentagon Technologies Group',
      subtitle: 'Environmental Systems & Recipe Control',
      description: 'For a parts cleaning system at Pentagon Technologies, the goal was to create a straightforward layout where operators could quickly choose the right wash cycle based on the specific parts they were loading. The workflow centered around a set of preset options, so I had to make sure the navigation was incredibly intuitive. \n\nThrough user testing on the floor, I realized how important it was to minimize the number of taps needed to start a cycle, so I designed the interface to prioritize the most common presets and added clear visual feedback for the cycle progress. This made the machine much faster to operate and kept the screen clean, simple, and free of unnecessary configurations.',
      image: new URL('../images/pentagonhmi.png', import.meta.url).href,
      icon: new URL('../images/pentagon.png', import.meta.url).href,
      tags: ['Dashboard', 'Data Visualization', 'Web Design']
    }
  ];

  return (
    <div className="home">
      {/* Hero Section with Split Layout */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-left">
            <h1 className="hero-greeting">Hello,</h1>
            <h1 className="hero-name">I'm George.</h1>
          </div>
          <div className="hero-right">
            <div className="hero-description">
              <p className="hero-bio">
                I'm a User Interface Designer from Ireland striving to provide both functional and beautiful interfaces, 
                with the goal of bridging the gap between people and technology. 
                I'm passionate about learning new approaches to problem-solving by adopting new technologies and software.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll Indicator Arrow */}
      <div className="scroll-indicator">
        <svg className="arrow-icon" width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 15 L20 25 L30 15" stroke="#9AA5CE" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Case Studies Section */}
      <section className="case-studies-section">
        <h2 className="section-title">Featured Projects</h2>
        <div className="case-studies-grid">
          {caseStudies.map((study) => (
            <div key={study.id} className="case-study-card">
              {study.icon && (
                <img src={study.icon} alt="" className="card-logo" />
              )}
              <div className="card-content">
                <h3 className="card-title">{study.title}</h3>
                <p className="card-subtitle">{study.subtitle}</p>
                <p className="card-description">{study.description}</p>
                <button className="view-project-btn">View Case Study</button>
              </div>
              <div className="card-image">
                <img src={study.image} alt={study.title} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
